import { useCallback } from "react";
import {
  Navigate,
  NavigateOptions,
  useNavigate as useRouterNavigate,
  useLocation as useRouterLocation,
} from "react-router-dom";

import { Flow, Routes as AppRoutes, StorageKey } from "@ctypes";
import { getImpactPhoneNumber, getMissingFieldRoute } from "@utils";
import { EntryShell } from "./EntryShell";
import {
  OnboardingProvider,
  getPersistedFlowState,
  useOnboardingContext,
  useFlowNavigate,
} from "./OnboardingContext";
import { AUTH_STEPS, BACK_HISTORY, BACK_TARGET, ENTRY_PATHS, ONBOARDING_STEPS } from "./steps";
import { STEP_COMPONENTS } from "./stepMap";

export { ENTRY_PATHS };

const ONBOARDING_PERSIST_KEY = StorageKey.OnboardingFlowState;

// ---------------------------------------------------------------------------
// Auth flow — pre-login screens at /auth
// ---------------------------------------------------------------------------

export const AuthFlow = () => {
  const location = useRouterLocation();
  const token = localStorage.getItem(StorageKey.Token);
  const user = localStorage.getItem(StorageKey.User);

  if (token && user) {
    const data = JSON.parse(user);
    const missing = getMissingFieldRoute(data);
    if (missing) return <Navigate to={AppRoutes.Onboarding} replace />;
    return <Navigate to={AppRoutes.Dashboard} replace />;
  }

  const locationState = location.state as { initialStep?: string } | null;

  // A phoneNumber in the query string drives the whole phone login: prefill the number, send the
  // code and land on the code screen without a tap. Note this means any crafted /auth?phoneNumber=
  // link texts a code to whatever number it carries, with no rate limit in front of it.
  //
  // An Impact link cannot carry phoneNumber (Impact drops it on redirect), so there the number
  // rides in sharedid instead and is treated exactly the same.
  const phoneNumberParam =
    new URLSearchParams(location.search).get("phoneNumber") ??
    getImpactPhoneNumber(location.search);

  const initialStep =
    locationState?.initialStep ?? (phoneNumberParam ? AppRoutes.PhoneLoginInput : AppRoutes.Auth);

  const initialState = phoneNumberParam
    ? { ...locationState, phoneNumber: phoneNumberParam, autoSubmit: true }
    : locationState;

  return <AuthFlowInner initialStep={initialStep} initialState={initialState} />;
};

const AuthFlowInner = ({
  initialStep,
  initialState,
}: {
  initialStep: string;
  initialState: any;
}) => {
  const routerNavigate = useRouterNavigate();

  const handleExitStep = useCallback(
    (step: string, options?: NavigateOptions) => {
      if (ONBOARDING_STEPS.has(step)) {
        routerNavigate(AppRoutes.Onboarding, {
          ...options,
          state: { initialStep: step, ...(typeof options?.state === "object" ? options.state : {}) },
        });
      } else {
        routerNavigate(step, options);
      }
    },
    [routerNavigate],
  );

  return (
    <OnboardingProvider
      initialStep={initialStep}
      initialState={initialState}
      steps={AUTH_STEPS}
      onExitStep={handleExitStep}
    >
      <AuthContent />
    </OnboardingProvider>
  );
};

const AuthContent = () => {
  const ctx = useOnboardingContext();
  const navigate = useFlowNavigate();

  if (!ctx) return null;

  const { currentStep } = ctx;

  const backTarget = BACK_TARGET[currentStep];
  const onBack = backTarget
    ? () => navigate(backTarget)
    : BACK_HISTORY.has(currentStep)
      ? () => navigate(-1)
      : undefined;

  const header = currentStep === AppRoutes.Auth ? "bare" : "pill";

  const Component = STEP_COMPONENTS[currentStep];

  if (!Component) {
    return <Navigate to={AppRoutes.Auth} replace />;
  }

  return (
    <EntryShell header={header} onBack={onBack}>
      <Component />
    </EntryShell>
  );
};

// ---------------------------------------------------------------------------
// Onboarding flow — post-login KYC screens at /onboarding
// ---------------------------------------------------------------------------

export const OnboardingFlow = () => {
  const location = useRouterLocation();
  const token = localStorage.getItem(StorageKey.Token);
  const user = localStorage.getItem(StorageKey.User);

  if (!token || !user) {
    return <Navigate to={AppRoutes.Auth} replace />;
  }

  const locationState = location.state as { initialStep?: string } | null;
  const persisted = getPersistedFlowState(ONBOARDING_PERSIST_KEY);

  // location.state survives a reload unchanged — every step-to-step move
  // within onboarding happens via internal state (goTo), never touching the
  // router, so location.state stays frozen at whatever step the one-time
  // auth->onboarding handoff happened to land on. Its presence alone isn't
  // proof of a fresh navigation. A genuinely new navigation gets a new
  // location.key; a reload replays the same key. Only trust location.state
  // when the key doesn't match what was persisted alongside it, and always
  // pull step + state from the same source so they can't get mismatched.
  const isFreshNavigation = !persisted || persisted.locationKey !== location.key;

  const initialStep = isFreshNavigation
    ? (locationState?.initialStep ?? getMissingFieldRoute(JSON.parse(user)))
    : (persisted?.step ?? getMissingFieldRoute(JSON.parse(user)));
  const initialState = isFreshNavigation ? (locationState ?? null) : (persisted?.state ?? null);

  if (!initialStep) {
    return <Navigate to={AppRoutes.Dashboard} replace />;
  }

  return <OnboardingFlowInner initialStep={initialStep} initialState={initialState} />;
};

const OnboardingFlowInner = ({
  initialStep,
  initialState,
}: {
  initialStep: string;
  initialState: any;
}) => {
  const routerNavigate = useRouterNavigate();

  const handleExitStep = useCallback(
    (step: string, options?: NavigateOptions) => {
      routerNavigate(step, options);
    },
    [routerNavigate],
  );

  return (
    <OnboardingProvider
      initialStep={initialStep}
      initialState={initialState}
      steps={ONBOARDING_STEPS}
      onExitStep={handleExitStep}
      persistKey={ONBOARDING_PERSIST_KEY}
    >
      <OnboardingContent />
    </OnboardingProvider>
  );
};

const OnboardingContent = () => {
  const ctx = useOnboardingContext();
  const navigate = useFlowNavigate();

  if (!ctx) return null;

  const { currentStep, currentState } = ctx;
  const flow = (currentState as { flow?: Flow } | null)?.flow;

  const isPushedBeneficiaryPicker =
    currentStep === AppRoutes.AddBeneficiaryType && flow != null && flow !== Flow.Onboarding;

  // The intake steps are shared with the pre-auth journey, so their back targets can point at
  // /auth. Here the account already exists, and /auth would bounce straight back through the
  // missing-field router to this same step -- offer no back button rather than a loop.
  const rawBackTarget = BACK_TARGET[currentStep];
  const backTarget = rawBackTarget === AppRoutes.Auth ? undefined : rawBackTarget;
  const onBack = backTarget
    ? () => navigate(backTarget)
    : BACK_HISTORY.has(currentStep) || isPushedBeneficiaryPicker
      ? () => navigate(-1)
      : undefined;

  const Component = STEP_COMPONENTS[currentStep];

  if (!Component) {
    return <Navigate to={AppRoutes.Dashboard} replace />;
  }

  return (
    <EntryShell header="pill" onBack={onBack}>
      <Component />
    </EntryShell>
  );
};
